/**
 * Holidays, birthdays, and the anniversary of the day this account first
 * spoke to her.
 *
 * `festive` is the mood with the widest set of triggers and the narrowest
 * register. It fires on any calendar holiday, either resident's birthday,
 * Katė's, and — newly, since nothing was passing it before — the account's own
 * anniversary. All of those have one thing in common that the lines can lean
 * on: the food rules are suspended today and everybody knows it.
 *
 * So she does not tell anyone to be careful. A narrator who spends Kūčios
 * reminding you about a calorie budget is not a flatmate, she is an app. What
 * she does instead is turn up, notice, and count nothing out loud.
 *
 * The specific days that get a whole skin — Vėlinės, Kūčios, Kalėdos,
 * Užgavėnės — have their own exclusive lines on top of this; see
 * `exclusive.ts`. This is the register for every other festive day.
 */

import { overBy } from '../fmt.js'
import type { Line } from './index.js'

export const FESTIVE_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  // A date she cares about, and somebody asked how she is on it.
  'chip_comeback.otu': [
    () => 'Šiandien — puikiai. Man tokios dienos tinka labiau nei žmonėms.',
    () => 'Geriau nei paprastai. Proga yra proga, net jei ji ne mano.',
    () => 'Šventiškai. Neklausk, kaip tai atrodo iš vidaus.',
  ],
  'chip_comeback.daryt': [
    () => 'Šiandien? Valgyk. Rytoj skaičiuosim, o šiandien tai net ne mano darbas.',
    () => 'Nieko. Šventę reikia suvalgyti, o ne suplanuoti.',
  ],
  'log_success.base': [
    () => 'Įrašyta. Šiandien šventė — skaičiuoju, bet nekomentuoju.',
    () => 'Užrašiau ir nutylėsiu. Šitokia diena.',
    () => 'Priimta. Šiandien biudžetas — tik rekomendacija. Kaip ir kasdien, tik garsiau.',
    () => 'Gerai. Šventinis įrašas. Jokių pastabų, tik apskaita.',
    () => 'Užrašyta. Per tokias dienas net aš turiu gerą nuotaiką. Neįprask.',
    () => 'Įrašiau. Šiandien tave gina kalendorius, ne aš.',
    () => 'Padėta į vietą. Šventė, tai ir tyliu. Rytoj kalbėsim.',
    () => 'Gerai. Švęsk. Aš čia dėl archyvo, ne dėl moralų.',
  ],
  'greeting.morning': [
    () => 'Rytas ir šventė. Šiandien nieko iš tavęs nenoriu, tik įrašyk.',
    () => 'Šventinis rytas. Aš irgi kartais mėgstu datas, kurios ką nors reiškia.',
    () => 'Rytas. Šiandien visi apsimeta, kad skaičių nėra. Aš pasitraukiu į šoną.',
  ],
  'greeting.day': [
    () => 'Šventės vidurdienis. Virtuvėje šiandien daugiau gyvybės nei manyje.',
    () => 'Diena. Šiandien šventė, o šventė — vienintelė priežastis, dėl kurios tyliu.',
  ],
  'greeting.evening': [
    () => 'Vakaras. Šventinis stalas jau padarė savo. Įrašyk, kiek atsimeni.',
    () => 'Vakaras. Šiandien niekas neskaičiuoja. Aš irgi, bet aš vis tiek matau.',
  ],
  'day_over.small': [
    (c) => `Viršyta ${overBy(c)} kcal. Šventė. Užrašiau ir daugiau nieko nesakau.`,
    (c) => `${overBy(c)} kcal virš. Būtų keista, jei nebūtų.`,
  ],
  'day_over.big': [
    (c) => `Viršyta ${overBy(c)} kcal. Šventinė norma. Rytoj kalendorius tavęs nebegins.`,
    (c) => `${overBy(c)} kcal. Šiandien tai net ne nusižengimas, o tradicija.`,
  ],
  'empty_today.evening': [
    () => 'Tuščia per šventę. Suprantu — bet vieno įrašo vis tiek paprašysiu.',
    () => 'Nieko neužrašyta. Šventės tai neatšaukia, tik pateisina.',
  ],
}
